import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Quiz, QuizQuestion, QuizQuestionOption } from '@/types/quiz';

export const useQuizManagement = (programId?: string) => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchQuizzes = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('quizzes')
        .select('*')
        .order('created_at', { ascending: false });

      if (programId) {
        query = query.eq('program_id', programId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setQuizzes(data || []);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      toast.error('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  const createQuiz = async (quizData: Omit<Quiz, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      
      const { data, error } = await supabase
        .from('quizzes')
        .insert([quizData])
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Quiz created successfully');
      await fetchQuizzes();
      return data;
    } catch (error) {
      console.error('Error creating quiz:', error);
      toast.error('Failed to create quiz');
      throw error;
    }
  };

  const updateQuiz = async (id: string, updates: Partial<Quiz>) => {
    try {
      
      const { data, error } = await supabase
        .from('quizzes')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Quiz updated successfully');
      await fetchQuizzes();
      return data;
    } catch (error) {
      console.error('Error updating quiz:', error);
      toast.error('Failed to update quiz');
      throw error;
    }
  };

  const deleteQuiz = async (id: string) => {
    try {
      
      const { error } = await supabase
        .from('quizzes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Quiz deleted successfully');
      await fetchQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Failed to delete quiz');
      throw error;
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  return {
    quizzes,
    loading,
    fetchQuizzes,
    createQuiz,
    updateQuiz,
    deleteQuiz
  };
};

export const useQuizQuestions = (quizId?: string) => {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchQuestions = useCallback(async () => {
    if (!quizId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quiz_questions')
        .select(`
          *,
          options:quiz_question_options(*)
        `)
        .eq('quiz_id', quizId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setQuestions(data || []);
    } catch (error) {
      console.error('Error fetching quiz questions:', error);
      toast.error('Failed to load quiz questions');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  const createQuestion = async (
    questionData: Omit<QuizQuestion, 'id' | 'created_at' | 'updated_at'>, 
    options?: { option_text: string; is_correct: boolean; explanation?: string; }[]
  ) => {
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .insert([questionData])
        .select()
        .single();

      if (error) throw error;
      
      // Create options if provided
      if (options && options.length > 0) {
        for (let i = 0; i < options.length; i++) {
          const option = options[i];
          await supabase
            .from('quiz_question_options')
            .insert([{
              question_id: data.id,
              option_text: option.option_text,
              is_correct: option.is_correct,
              explanation: option.explanation,
              order_index: i
            }]);
        }
      }
      
      toast.success('Question created successfully');
      await fetchQuestions();
      return data;
    } catch (error) {
      console.error('Error creating question:', error);
      toast.error('Failed to create question');
      throw error;
    }
  };

  const updateQuestion = async (
    id: string, 
    updates: Partial<QuizQuestion>, 
    options?: { option_text: string; is_correct: boolean; explanation?: string; }[]
  ) => {
    try {
      // Update the question
      const { data, error } = await supabase
        .from('quiz_questions')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      // Update options if provided
      if (options && options.length > 0) {
        // Get existing options
        const { data: existingOptions, error: fetchError } = await supabase
          .from('quiz_question_options')
          .select('*')
          .eq('question_id', id)
          .order('order_index');

        if (fetchError) throw fetchError;

        // Update existing options and create new ones
        for (let i = 0; i < options.length; i++) {
          const option = options[i];
          
          if (i < (existingOptions?.length || 0)) {
            // Update existing option
            const { error: updateError } = await supabase
              .from('quiz_question_options')
              .update({
                option_text: option.option_text,
                is_correct: option.is_correct,
                explanation: option.explanation,
                order_index: i
              })
              .eq('id', existingOptions![i].id);

            if (updateError) throw updateError;
          } else {
            // Create new option
            const { error: insertError } = await supabase
              .from('quiz_question_options')
              .insert([{
                question_id: id,
                option_text: option.option_text,
                is_correct: option.is_correct,
                explanation: option.explanation,
                order_index: i
              }]);

            if (insertError) throw insertError;
          }
        }

        // Delete extra options only if they're not referenced by quiz answers
        if (existingOptions && existingOptions.length > options.length) {
          for (let i = options.length; i < existingOptions.length; i++) {
            // Check if this option is referenced by any quiz answers
            const { data: referencedAnswers, error: checkError } = await supabase
              .from('quiz_answers')
              .select('id')
              .eq('selected_option_id', existingOptions[i].id)
              .limit(1);

            if (checkError) throw checkError;

            // Only delete if not referenced
            if (!referencedAnswers || referencedAnswers.length === 0) {
              const { error: deleteError } = await supabase
                .from('quiz_question_options')
                .delete()
                .eq('id', existingOptions[i].id);

              if (deleteError) throw deleteError;
            }
          }
        }
      }
      
      toast.success('Question updated successfully');
      await fetchQuestions();
      return data;
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
      throw error;
    }
  };

  const deleteQuestion = async (id: string) => {
    try {
      const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Question deleted successfully');
      await fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
      throw error;
    }
  };

  const createOption = async (optionData: Omit<QuizQuestionOption, 'id' | 'created_at'>) => {
    try {
      const { data, error } = await supabase
        .from('quiz_question_options')
        .insert([optionData])
        .select()
        .single();

      if (error) throw error;
      
      await fetchQuestions();
      return data;
    } catch (error) {
      console.error('Error creating option:', error);
      toast.error('Failed to create option');
      throw error;
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return {
    questions,
    loading,
    fetchQuestions,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    createOption
  };
};